import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { ReactNode, RefObject, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { QueryKey } from '@/Config/api/queryKey';
import { Usage } from '@/Domain/api/Subscription/dto/SubscriptionUsage.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  CREDIT_TOPUP_ENTRYPOINT_BILLING,
  CREDIT_TOPUP_PANEL_ANCHOR,
} from '@/Domain/components/Subscription/CreditTopup/creditTopupAnalytics';
import { useCreditTopupCheckout } from '@/Domain/components/Subscription/CreditTopup/useCreditTopupCheckout';
import { isAgentOutOfCredits } from '@/Domain/components/Subscription/creditAffordability';
import {
  buildBillingTabPath,
  UPGRADE_ENTRYPOINT_AGENT_MUTED_BANNER,
} from '@/Domain/components/Subscription/upgradeFunnelAnalytics';
import { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useCreditTopupOffer } from '@/Domain/hooks/Subscription/useCreditTopupOffer.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';
import { formatCurrency } from '@/Domain/utils/formatCurrency';

// Overrides the app-wide `staleTime: Infinity`, which would freeze the bootstrap seed all session.
const USAGE_STALE_MS = 60_000;
const USAGE_POLL_MUTED_MS = 60_000;
const USAGE_POLL_HEALTHY_MS = 300_000;

type Remedy = 'offer' | 'credits' | 'upgrade';
type CtaDestination = 'checkout' | 'billing';

// The description is hidden below `lg` and truncated above it, so the price can be off screen.
const isShownInFull = (element: HTMLElement | null): boolean =>
  !!element && element.getClientRects().length > 0 && element.scrollWidth <= element.clientWidth;

// Mirrors ShopifyReviewPrompt, the sibling in this DefaultLayout slot: flat at rest, border and
// tonal fill rather than a shadow, and the one saturated colour rationed to the icon chip.
const MutedBanner = ({
  description,
  descriptionRef,
  action,
}: {
  description: string;
  descriptionRef?: RefObject<HTMLSpanElement>;
  action?: ReactNode;
}) => {
  const { t } = useTranslation('agent_muted_banner');

  return (
    <div
      className="flex items-center gap-3 border-b border-border bg-card px-4 py-2.5"
      role="alert"
      data-testid="agent-muted-banner"
    >
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-destructive-muted">
        <TriangleAlert className="size-4 text-destructive" />
      </span>

      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="min-w-0 truncate text-sm font-medium text-foreground">{t('title')}</span>
        <span
          ref={descriptionRef}
          className="hidden min-w-0 truncate text-sm text-muted-foreground lg:inline"
          data-testid="agent-muted-banner-description"
        >
          {description}
        </span>
      </div>

      {action}
    </div>
  );
};

const BillingAdminBanner = () => {
  const { t } = useTranslation('agent_muted_banner');
  const { scenario } = useParams<{ scenario: string }>();
  const navigate = useNavigate();
  const { capture } = usePostHogAnalytics();
  // Admin-only endpoint, so this must stay behind the permission branch or it is a guaranteed 403.
  const { offer, isEligible, isLoading } = useCreditTopupOffer();
  const { mutate: startCheckout, isPending } = useCreditTopupCheckout(offer);
  const descriptionRef = useRef<HTMLSpanElement>(null);

  const canNamePrice = isEligible && !!offer?.credits && !!offer?.price_usd;
  // Top-ups need a live Discord deployment, so for most owners buying credits is not purchasable
  // and the only route back is a bigger plan.
  const remedy: Remedy = canNamePrice ? 'offer' : isEligible ? 'credits' : 'upgrade';

  // Holding the banner until the offer resolves stops the remedy flipping under the reader.
  if (isLoading) {
    return null;
  }

  return (
    <MutedBanner
      descriptionRef={descriptionRef}
      description={
        canNamePrice
          ? t('description_with_offer', {
              credits: offer.credits.toLocaleString(),
              price: `${formatCurrency(offer.price_usd)} ${t('subscription_page:pricing.excl_vat')}`,
            })
          : isEligible
            ? t('description')
            : t('description_upgrade')
      }
      action={
        <Button
          variant="dark"
          size="sm"
          className="shrink-0"
          data-testid="agent-muted-banner-cta"
          loading={isPending}
          onClick={() => {
            // Stripe only after the owner has seen the price; otherwise Billing shows it, at the pack.
            const destination: CtaDestination =
              remedy === 'offer' && isShownInFull(descriptionRef.current) ? 'checkout' : 'billing';
            capture(PostHogEventEnum.AgentMutedBannerCtaClicked, { remedy, destination });
            if (destination === 'checkout') {
              startCheckout({ auto_reload: false, entrypoint: CREDIT_TOPUP_ENTRYPOINT_BILLING });
              return;
            }
            const billingPath = buildBillingTabPath(
              scenario!,
              UPGRADE_ENTRYPOINT_AGENT_MUTED_BANNER,
            );
            navigate(
              remedy === 'upgrade' ? billingPath : `${billingPath}#${CREDIT_TOPUP_PANEL_ANCHOR}`,
            );
          }}
        >
          {canNamePrice ? t('cta_with_offer') : isEligible ? t('cta') : t('cta_upgrade')}
        </Button>
      }
    />
  );
};

const ReadOnlyBanner = () => {
  const { t } = useTranslation('agent_muted_banner');

  return <MutedBanner description={t('description_no_permission')} />;
};

export const AgentMutedBanner = () => {
  const { capture } = usePostHogAnalytics();
  const { data: subscriptionData } = useGetSubscription();
  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });

  const tier = subscriptionData?.current_plan.tier;
  const permissions = aiAssistants?.current_ai_assistant?.permissions;
  // Resolved, not just non-CUSTOM: `undefined` would otherwise read as "not Enterprise".
  const bannerApplies = !!tier && tier !== TierEnum.CUSTOM && !!permissions;

  const { data: usage } = useQuery<Usage>({
    queryKey: [QueryKey.subscription.usage],
    // Explicit gate: no poll unless a banner is possible. Siblings observe this key regardless.
    enabled: bannerApplies,
    staleTime: USAGE_STALE_MS,
    refetchInterval: (query) =>
      isAgentOutOfCredits(query.state.data) ? USAGE_POLL_MUTED_MS : USAGE_POLL_HEALTHY_MS,
  });

  const isMuted = bannerApplies && isAgentOutOfCredits(usage);
  const canManageBilling = !!permissions?.subscription;

  useEffect(() => {
    if (isMuted) {
      capture(PostHogEventEnum.AgentMutedBannerViewed, {
        can_manage_billing: canManageBilling,
      });
    }
  }, [isMuted, canManageBilling, capture]);

  if (!isMuted) {
    return null;
  }

  return canManageBilling ? <BillingAdminBanner /> : <ReadOnlyBanner />;
};
